import React, { useEffect, useState, useCallback } from 'react';
import AdminLayout from '../../components/layout/AdminLayout';
import Modal from '../../components/common/Modal';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import StatusBadge from '../../components/common/StatusBadge';
import RichTextEditor from '../../components/common/RichTextEditor';
import ImageWithFallback from '../../components/common/ImageWithFallback';
import { useAuth } from '../../context/AuthContext';
import {
  getBrandList, createBrand, updateBrand, deleteBrand,
  uploadBrandLogo, uploadBrandMain, getBrandLogoUrl, getBrandMainUrl,
} from '../../api/brand';
import { formatDateTime } from '../../utils/format';

/**
 * 브랜드(프랜차이즈) 관리 화면.
 * 브랜드명 + 로고 이미지 + 대표(배너) 이미지 + 소개글(에디터 HTML) + 홈페이지/SNS 링크로 구성된다.
 * 이미지는 배너와 동일 패턴으로 브랜드를 먼저 저장한 뒤 반환된 brandId 로 이어서 업로드한다.
 * 등록한 브랜드는 매장관리에서 매장에 배정하고, 고객 QR오더 화면의 "브랜드 소개" 섹션에 노출된다.
 */
// 헤더 검색창 좌측 돋보기 아이콘
function SearchIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" />
    </svg>
  );
}

export default function BrandManagePage() {
  const { auth } = useAuth();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [keyword, setKeyword] = useState('');

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getBrandList();
      setList(res.data || []);
    } catch (err) {
      setError(err.response?.data?.message || '브랜드 목록을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchList(); }, [fetchList]);

  const uploadImages = async (brandId, logoFile, mainFile) => {
    if (logoFile) await uploadBrandLogo(brandId, logoFile, auth.adminName);
    if (mainFile) await uploadBrandMain(brandId, mainFile, auth.adminName);
  };

  const handleCreate = async (form, logoFile, mainFile) => {
    try {
      const res = await createBrand({ ...form, createdBy: auth.adminName });
      const brandId = res.data?.brandId;
      if (brandId) await uploadImages(brandId, logoFile, mainFile);
      setCreating(false);
      fetchList();
    } catch (err) {
      window.alert(err.response?.data?.message || '브랜드 등록에 실패했습니다.');
    }
  };

  const handleUpdate = async (form, logoFile, mainFile) => {
    try {
      await updateBrand(editing.brandId, { ...form, updatedBy: auth.adminName });
      await uploadImages(editing.brandId, logoFile, mainFile);
      setEditing(null);
      fetchList();
    } catch (err) {
      window.alert(err.response?.data?.message || '브랜드 수정에 실패했습니다.');
    }
  };

  const handleDelete = async () => {
    try {
      await deleteBrand(deleteTarget.brandId);
      setDeleteTarget(null);
      fetchList();
    } catch (err) {
      window.alert(err.response?.data?.message || '브랜드 삭제에 실패했습니다. (이 브랜드를 사용하는 매장이 있으면 먼저 해제하세요)');
    }
  };

  // 헤더 검색: 브랜드명 부분일치(대소문자 무시)
  const filtered = list.filter((b) => !keyword || (b.brandName || '').toLowerCase().includes(keyword.trim().toLowerCase()));

  return (
    <AdminLayout title="브랜드관리" menuUrl="/admin/brand">
      {/* 타이틀 영역 */}
      <div className="page-header">
        <div>
          <h1>브랜드관리</h1>
          <div className="subtitle">프랜차이즈 브랜드와 소개 정보를 등록합니다. 매장관리에서 매장에 브랜드를 배정하면 고객 QR오더 화면에 브랜드 소개가 노출됩니다.</div>
        </div>
      </div>

      {/* 헤더 영역: 검색 + 건수 / 등록 액션 */}
      <div className="list-toolbar">
        <div className="list-toolbar-left">
          <div className="list-search">
            <SearchIcon />
            <input
              className="input"
              placeholder="브랜드명 검색"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
            />
          </div>
        </div>
        <div className="list-toolbar-right">
          <button className="btn btn-primary" onClick={() => setCreating(true)}>+ 브랜드 등록</button>
        </div>
      </div>

      {/* 리스트 영역 */}
      <div className="list-count-bar">
        <span className="list-count">전체 <b>{filtered.length}</b>건</span>
      </div>

      <div className="card">
        <div className="table-wrap">
          <table className="data-table">
            <thead><tr><th>로고</th><th>브랜드명</th><th>홈페이지</th><th>노출</th><th>등록일</th><th>관리</th></tr></thead>
            <tbody>
              {loading && <tr><td colSpan={6} className="empty-row">불러오는 중...</td></tr>}
              {!loading && error && <tr><td colSpan={6} className="empty-row error-state">{error}</td></tr>}
              {!loading && !error && list.length === 0 && <tr><td colSpan={6} className="empty-row">등록된 브랜드가 없습니다.</td></tr>}
              {!loading && !error && list.length > 0 && filtered.length === 0 && <tr><td colSpan={6} className="empty-row">검색 결과가 없습니다.</td></tr>}
              {!loading && !error && filtered.map((b) => (
                <tr key={b.brandId}>
                  <td>
                    <div style={{ width: 56, height: 56, borderRadius: 8, overflow: 'hidden', background: 'var(--color-surface-alt)' }}>
                      {b.logoImagePath && (
                        <ImageWithFallback src={getBrandLogoUrl(b.brandId)} alt={b.brandName} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                      )}
                    </div>
                  </td>
                  <td>{b.brandName}</td>
                  <td className="text-secondary" style={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.homepageUrl || '-'}</td>
                  <td><StatusBadge value={b.useYn} label={b.useYn === 'Y' ? '노출' : '숨김'} /></td>
                  <td>{formatDateTime(b.createdAt)}</td>
                  <td className="actions">
                    <button className="btn btn-secondary btn-sm" onClick={() => setEditing(b)}>수정</button>
                    <button className="btn btn-danger btn-sm" onClick={() => setDeleteTarget(b)}>삭제</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {creating && <BrandFormModal onClose={() => setCreating(false)} onSave={handleCreate} />}
      {editing && <BrandFormModal brand={editing} onClose={() => setEditing(null)} onSave={handleUpdate} />}
      {deleteTarget && (
        <ConfirmDialog title="브랜드 삭제" message={`'${deleteTarget.brandName}' 브랜드를 삭제하시겠습니까?`}
          confirmLabel="삭제" danger onCancel={() => setDeleteTarget(null)} onConfirm={handleDelete} />
      )}
    </AdminLayout>
  );
}

function ImagePicker({ id, label, previewUrl, onSelect, wide }) {
  return (
    <div className="field">
      <label>{label}</label>
      <div
        onClick={() => document.getElementById(id)?.click()}
        style={{
          width: '100%', maxWidth: wide ? 420 : 160, height: wide ? 140 : 160, borderRadius: 8,
          border: '1.5px dashed var(--color-border)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', overflow: 'hidden', background: 'var(--color-surface-alt)',
        }}
      >
        {previewUrl ? (
          <ImageWithFallback src={previewUrl} alt="미리보기" style={{ width: '100%', height: '100%', objectFit: wide ? 'cover' : 'contain' }} />
        ) : (
          <span className="text-secondary" style={{ fontSize: 12 }}>클릭하여 이미지 선택</span>
        )}
      </div>
      <input id={id} type="file" accept="image/*" hidden onChange={onSelect} />
    </div>
  );
}

function BrandFormModal({ brand, onClose, onSave }) {
  const isNew = !brand;
  const [form, setForm] = useState({
    brandName: brand?.brandName || '',
    foodtechEnterpriseCode: brand?.foodtechEnterpriseCode || '',
    foodtechCorporationCode: brand?.foodtechCorporationCode || '',
    introContent: brand?.introContent || '',
    homepageUrl: brand?.homepageUrl || '',
    snsUrl: brand?.snsUrl || '',
    useYn: brand?.useYn || 'Y',
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [mainFile, setMainFile] = useState(null);
  const [mainPreview, setMainPreview] = useState(null);
  const [saving, setSaving] = useState(false);

  const pick = (setFile, setPreview) => (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFile(file);
    setPreview(URL.createObjectURL(file));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave({ ...form }, logoFile, mainFile);
    } finally {
      setSaving(false);
    }
  };

  const existingLogo = !isNew && brand.logoImagePath ? getBrandLogoUrl(brand.brandId) : null;
  const existingMain = !isNew && brand.mainImagePath ? getBrandMainUrl(brand.brandId) : null;

  return (
    <Modal title={isNew ? '브랜드 등록' : '브랜드 수정'} onClose={onClose} width={680}
      footer={<>
        <button className="btn btn-secondary" onClick={onClose}>취소</button>
        <button className="btn btn-primary" onClick={submit} disabled={saving}>{saving ? '저장 중...' : '저장'}</button>
      </>}>
      <form onSubmit={submit}>
        <div className="field">
          <label>브랜드명</label>
          <input className="input" value={form.brandName} onChange={(e) => setForm({ ...form, brandName: e.target.value })} required placeholder="예: 우지커피" />
        </div>

        <div className="field-row">
          <div className="field">
            <label>푸드테크 가맹본부코드 <span style={{ fontWeight: 400, color: 'var(--color-text-tertiary)' }}>(선택)</span></label>
            <input className="input" value={form.foodtechEnterpriseCode} onChange={(e) => setForm({ ...form, foodtechEnterpriseCode: e.target.value })} placeholder="enterpriseCode" />
          </div>
          <div className="field">
            <label>푸드테크 본부코드 <span style={{ fontWeight: 400, color: 'var(--color-text-tertiary)' }}>(선택)</span></label>
            <input className="input" value={form.foodtechCorporationCode} onChange={(e) => setForm({ ...form, foodtechCorporationCode: e.target.value })} placeholder="corporationCode" />
          </div>
        </div>

        <div className="field-row" style={{ gap: 24, alignItems: 'flex-start' }}>
          <ImagePicker id="brand-logo-input" label="로고 이미지" previewUrl={logoPreview || existingLogo} onSelect={pick(setLogoFile, setLogoPreview)} />
          <ImagePicker id="brand-main-input" label="대표 이미지 (선택)" previewUrl={mainPreview || existingMain} onSelect={pick(setMainFile, setMainPreview)} wide />
        </div>

        <div className="field">
          <label>브랜드 소개</label>
          <RichTextEditor
            value={form.introContent}
            onChange={(html) => setForm({ ...form, introContent: html })}
            placeholder="브랜드 스토리·소개를 입력하세요"
          />
        </div>

        <div className="field">
          <label>홈페이지 URL (선택)</label>
          <input className="input" value={form.homepageUrl} onChange={(e) => setForm({ ...form, homepageUrl: e.target.value })} placeholder="https://" />
        </div>

        <div className="field">
          <label>SNS URL (선택)</label>
          <input className="input" value={form.snsUrl} onChange={(e) => setForm({ ...form, snsUrl: e.target.value })} placeholder="https://instagram.com/..." />
        </div>

        <div className="field checkbox-row">
          <input type="checkbox" id="brandUseYn" checked={form.useYn === 'Y'} onChange={(e) => setForm({ ...form, useYn: e.target.checked ? 'Y' : 'N' })} />
          <label htmlFor="brandUseYn" style={{ margin: 0 }}>노출 (QR 화면 브랜드 소개 노출)</label>
        </div>
      </form>
    </Modal>
  );
}
